import { jsx, jsxs } from "react/jsx-runtime";
import { useMemo } from "react";
import { cn } from "../../../lib/utils.js";
import "../shared/quiz-blank.js";
import { KatexText } from "../shared/katex-text.js";
import { QuizResultFeedback } from "../shared/quiz-result-feedback.js";
import { SubmitButton } from "../shared/submit-button.js";
import { useTranslation } from "react-i18next";
import "../../../node_modules/.pnpm/hls.js@1.6.15/node_modules/hls.js/dist/hls.js";
import { QuizMedia } from "../shared/quiz-media.js";
import { QuizSolveDescription } from "../shared/quiz-solve-description.js";
import { DifficultyBadge, DebugQuizId } from "../shared/debug-quiz-id.js";
import { formatProblem } from "../../../utils/quiz-parser.js";
import { useDragTextQuiz } from "../hooks/use-drag-text-quiz.js";
import { motion } from "../../../node_modules/.pnpm/framer-motion@11.18.2_react-dom@18.3.1_react@18.3.1__react@18.3.1/node_modules/framer-motion/dist/es/render/components/motion/proxy.js";
function DragTextStudent({
  quizId,
  quiz,
  onSolve,
  solveResult,
  showReallyTrue = false,
  isViewSolved = false,
  initialAnswers: initialAnswersProp,
  submitLabel,
  hideSubmitButton = false,
  onAnswerChange,
  disableSound = false,
  difficultyLevel
}) {
  var _a;
  const { t } = useTranslation("quiz");
  const {
    answers,
    currentIndex,
    blankCount,
    problemKey,
    availableOptions,
    isSolved,
    canSubmit,
    shouldShowCorrectAnswers,
    isShowingCorrectAnswers,
    setIsShowingCorrectAnswers,
    resolveLabel,
    handleBlankClick,
    handleClearBlank,
    handleSelectOption,
    handleSubmit
  } = useDragTextQuiz({
    quizId,
    quiz,
    onSolve,
    solveResult,
    showReallyTrue,
    isViewSolved,
    initialAnswers: initialAnswersProp,
    onAnswerChange
  });
  const renderedProblem = useMemo(() => {
    if (answers.length === 0 && blankCount > 0) return [];
    const timestamp = problemKey;
    return formatProblem(
      quiz.problem,
      `${quizId}-problem-${timestamp}`,
      (index) => {
        var _a2, _b;
        const value = answers[index] || "";
        const valueLabel = resolveLabel(value);
        const isActive = currentIndex === index && !isSolved && !isViewSolved;
        const hasValue = value !== "";
        const isCorrect = isSolved ? (_a2 = solveResult == null ? void 0 : solveResult.corrects) == null ? void 0 : _a2[index] : null;
        const correctAnswer = ((_b = solveResult == null ? void 0 : solveResult.correctAnswers) == null ? void 0 : _b[index]) ?? "";
        const correctAnswerLabel = resolveLabel(correctAnswer);
        const isCompleted = isSolved || isViewSolved;
        return /* @__PURE__ */ jsx(
          "span",
          {
            onClick: () => handleBlankClick(index),
            className: cn(
              "min-w-[48px] px-4 relative my-1 text-[24px] font-medium rounded-2xl mx-2 inline-flex items-center justify-between h-12 border-2 border-[#BBBBBB] [box-shadow:0px_4px_0px_0px_rgba(0,0,0,0.1)] transition-all duration-200",
              // Hover (only when not completed)
              !isCompleted && "hover:text-[#55C3FF] hover:border-[#55C3FF] hover:[box-shadow:0px_4px_0px_0px_rgba(85,195,255,0.3)]",
              // Active (current index, no value)
              isActive && !hasValue && "min-w-[96px] bg-[#DDF3FF] border-[#55C3FF] [box-shadow:unset]",
              // Active (current index, has value)
              isActive && hasValue && "min-w-[96px] bg-[#DDF3FF] border-[#55C3FF] [box-shadow:unset]",
              // Has value, not active, not solved
              !isSolved && !isViewSolved && hasValue && !isActive && "bg-[#DDF3FF] not-[&:hover]:border-none [box-shadow:unset]",
              // Correct
              isSolved && isCorrect === true && "bg-[#D8F6EF] text-[#3BD0AE] border-none pointer-events-none [box-shadow:unset]",
              // Wrong (not showing correct answers)
              isSolved && isCorrect === false && !shouldShowCorrectAnswers && "bg-[#FFE1DF] text-[#FF675E] border-none pointer-events-none [box-shadow:unset]",
              // Showing correct answers for wrong blank
              shouldShowCorrectAnswers && isSolved && isCorrect === false && "bg-[#D8F6EF] text-[#3BD0AE] border-none pointer-events-none [box-shadow:unset]",
              // Completed (view solved)
              isViewSolved && "border-none pointer-events-none [box-shadow:unset]"
            ),
            children: shouldShowCorrectAnswers && isSolved && isCorrect === false ? /* @__PURE__ */ jsx(KatexText, { text: correctAnswerLabel }) : hasValue ? /* @__PURE__ */ jsxs("span", { className: "flex gap-1 items-center", children: [
              /* @__PURE__ */ jsx(KatexText, { text: valueLabel }),
              !isSolved && !isViewSolved && /* @__PURE__ */ jsx(
                "button",
                {
                  onClick: (e) => {
                    e.stopPropagation();
                    handleClearBlank(index);
                  },
                  className: "w-6 h-6 ml-2 bg-[#BBBBBB] rounded-full flex items-center justify-center shrink-0",
                  children: /* @__PURE__ */ jsx("svg", { width: "12", height: "12", viewBox: "0 0 12 12", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: /* @__PURE__ */ jsx("path", { d: "M1 1L11 11M11 1L1 11", stroke: "white", strokeWidth: "2", strokeLinecap: "round" }) })
                }
              )
            ] }) : /* @__PURE__ */ jsx("span", { className: "text-[#BBBBBB]", children: t("types.dragText.blank") })
          },
          `blank-${quizId}-${timestamp}-${index}`
        );
      },
      quiz.additionalImages ?? void 0
    );
  }, [answers, currentIndex, isSolved, solveResult, shouldShowCorrectAnswers, quiz.problem, quizId, isViewSolved, blankCount, handleBlankClick, handleClearBlank, problemKey, quiz.additionalImages, resolveLabel]);
  return /* @__PURE__ */ jsxs("div", { className: "space-y-6", children: [
    /* @__PURE__ */ jsxs(
      motion.div,
      {
        initial: { opacity: 0, y: -10 },
        animate: { opacity: 1, y: 0 },
        className: "space-y-2",
        children: [
          /* @__PURE__ */ jsxs("div", { className: "inline-flex gap-2 items-center", children: [
            /* @__PURE__ */ jsx("div", { className: "inline-flex gap-2 items-center px-3 py-1 text-sm font-bold text-white bg-[#55C3FF] rounded-lg", children: /* @__PURE__ */ jsx("span", { children: t("types.dragText.label") }) }),
            /* @__PURE__ */ jsx(DifficultyBadge, { level: difficultyLevel }),
            /* @__PURE__ */ jsx(DebugQuizId, { quizId })
          ] }),
          /* @__PURE__ */ jsx("h3", { className: "text-2xl font-bold leading-relaxed text-gray-900", children: /* @__PURE__ */ jsx(KatexText, { text: quiz.title }) }),
          /* @__PURE__ */ jsx(QuizMedia, { image: quiz.image, imageMobile: quiz.imageMobile, desmos: quiz.desmos, vars: quiz.vars })
        ]
      }
    ),
    /* @__PURE__ */ jsx(
      motion.div,
      {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        transition: { delay: 0.1 },
        className: "text-2xl font-medium text-gray-700",
        children: renderedProblem
      }
    ),
    !isSolved && !isViewSolved && availableOptions.length > 0 && /* @__PURE__ */ jsx(
      motion.div,
      {
        initial: { opacity: 0, y: 20 },
        animate: { opacity: 1, y: 0 },
        transition: { delay: 0.2 },
        className: "w-full bg-[#F0F0F0] p-6 rounded-2xl mt-10",
        children: /* @__PURE__ */ jsx("div", { className: "flex flex-row gap-3 flex-wrap", children: availableOptions.map((option, idx) => {
          const isCurrentlySelected = answers[currentIndex] === option.key;
          return /* @__PURE__ */ jsx(
            "button",
            {
              onClick: () => handleSelectOption(option.key),
              className: cn(
                "text-[24px] font-medium min-w-[48px] h-12 flex text-gray-700 items-center justify-center bg-white rounded-2xl border-2 border-[#BBBBBB] cursor-pointer [box-shadow:0px_4px_0px_#E0E0E0] px-4 py-3 transition-all duration-150",
                "hover:border-[#55C3FF] hover:text-[#55C3FF] hover:[box-shadow:0px_4px_0px_rgba(85,195,255,0.30)]",
                isCurrentlySelected && "border-[#55C3FF] bg-[#DDF3FF] [box-shadow:unset]"
              ),
              children: /* @__PURE__ */ jsx(KatexText, { text: option.label })
            },
            option.key || `option-${idx}`
          );
        }) })
      }
    ),
    !isSolved && !isViewSolved && !hideSubmitButton && /* @__PURE__ */ jsx(
      SubmitButton,
      {
        onClick: handleSubmit,
        disabled: !canSubmit,
        label: submitLabel
      }
    ),
    isSolved && /* @__PURE__ */ jsx(
      QuizResultFeedback,
      {
        isCorrect: (solveResult == null ? void 0 : solveResult.correct) ?? false,
        show: isSolved,
        correctAnswer: shouldShowCorrectAnswers ? (_a = solveResult == null ? void 0 : solveResult.correctAnswers) == null ? void 0 : _a.map((a) => resolveLabel(a)).join(", ") : void 0,
        disableSound
      }
    ),
    isSolved && (solveResult == null ? void 0 : solveResult.description) && /* @__PURE__ */ jsx(
      QuizSolveDescription,
      {
        description: solveResult.description,
        commentaryImages: quiz.commentaryImages,
        vars: quiz.vars,
        id: `${quizId}-desc`,
        actionVisible: !solveResult.correct,
        actionLabel: isShowingCorrectAnswers ? t("common.viewMyAnswer") : t("common.viewCorrectAnswer"),
        onActionClick: () => setIsShowingCorrectAnswers((v) => !v)
      }
    )
  ] });
}
export {
  DragTextStudent
};
//# sourceMappingURL=drag-text-student.js.map
